<template>
  <div class="space-y-4">
    <div class="grid gap-4 md:grid-cols-3">
      <div class="flex flex-col gap-4">
        <LabeledText :label="t('renter')">
          {{ booking.customerData?.firstName ?? 'n/a' }}
          {{ booking.customerData?.lastName ?? 'n/a' }}<br />
          <span>
            {{ t('tel') }}
            <PhoneLink :phone-number="booking.customerData?.phone" />
          </span>
        </LabeledText>
        <BookingsOfUserAmount
          v-if="showPreviousBookings && booking.customer"
          :user-id="booking.customer?.id"
        />
      </div>

      <div class="space-y-4">
        <LabeledText :label="t('bookingNumber')">
          <CopyToClipboardButton
            :text="booking.bookingNumber"
            class="my-2"
            data-testid="booking-number"
          >
            {{ booking.bookingNumber }}
          </CopyToClipboardButton>
        </LabeledText>
        <LabeledText v-if="booking.createdBy || booking.creationSource" :label="t('createdBy')">
          <template v-if="booking.createdBy">
            <component
              :is="$can('read', subject('User', booking.createdBy)) ? RouterLink : 'span'"
              :to="{ name: 'user', params: { id: booking.createdBy.id } }"
              :class="{
                'text-primary underline hover:no-underline': $can(
                  'read',
                  subject('User', booking.createdBy),
                ),
              }"
            >
              {{ formatUserDisplayName(booking.createdBy) }}
            </component>
          </template>
          <span v-if="booking.creationSource">
            {{
              booking.createdBy
                ? t('createdVia', { source: creationSourceLabel })
                : creationSourceLabel
            }}
          </span>
        </LabeledText>
        <LabeledText
          v-if="booking.agency"
          :label="t('externalReference')"
          :info-text="t('externalReferenceInfo')"
        >
          <BookingInlineField
            v-slot="{ defaultInputBinds }"
            :disabled="!$can('update', subject('Booking', booking), 'externalReference')"
            :booking="booking"
            field-name="externalReference"
            :validation-schema="yup.string().nullable()"
          >
            <InputField
              name="externalReference"
              variant="inline"
              v-bind="defaultInputBinds"
              class="my-2"
            />
          </BookingInlineField>
        </LabeledText>
        <LabeledText v-if="booking.insuranceCase" :label="t('insuranceCase')">
          <component
            :is="
              $can('read', subject('InsuranceCase', booking.insuranceCase)) ? RouterLink : 'span'
            "
            :to="{ name: 'insuranceCase', params: { id: booking.insuranceCase.id } }"
            :class="{
              'text-primary underline hover:no-underline': $can(
                'read',
                subject('InsuranceCase', booking.insuranceCase),
              ),
            }"
          >
            <span>{{
              t(`insuranceCaseRef.${booking.insuranceCase.insuranceProvider}`, {
                referenceNumber: booking.insuranceCase.insuranceData?.referenceNumber,
              })
            }}</span>
          </component>
        </LabeledText>
      </div>

      <div>
        <div v-if="showStatus" class="mb-2 rounded-md bg-white p-4">
          <div class="flex gap-2">
            <LabeledText class="flex-1" :label="t('bookingStatus')">
              <p class="flex items-center justify-start gap-2">
                <BookingStatusBadge :status="props.booking.status" size="md" />
                <Spinner
                  v-if="booking.status === BookingStatus.CONFIRMED && isSaving"
                  class="h-5 w-5"
                />
              </p>
            </LabeledText>
            <DropdownDotted
              v-if="dropdownDottedItems.length"
              :buttons="
                dropdownDottedItems.filter(
                  (e) =>
                    !(e.active === false || (e.statuses && !e.statuses?.includes(booking.status))),
                )
              "
              :is-loading="isRefreshingBookingConfirmation"
            />
          </div>

          <CVButton
            v-if="booking.status === BookingStatus.DRAFT && canUpdateToStatus(BookingStatus.OPEN)"
            class="mt-2 w-full"
            :is-loading="isSaving"
            @click.prevent="setBookingToPaid"
          >
            {{ t('setBookingToPaid') }}
          </CVButton>
          <CVButton
            v-if="
              (booking.status === BookingStatus.OPEN ||
                booking.status === BookingStatus.FLOATING) &&
              canUpdateToStatus(BookingStatus.CONFIRMED)
            "
            variant="default"
            class="mt-2 w-full"
            :is-loading="isSaving"
            @click.prevent="setBookingToConfirmed"
          >
            {{ t('confirmBooking') }}
          </CVButton>
          <CVButton
            v-if="canFinalizePeriodicInvoicingBooking"
            variant="default"
            class="mt-2 w-full"
            :is-loading="isSaving"
            @click.prevent="finalizePeriodicInvoicingBooking"
          >
            {{ t('finalizeBooking') }}
          </CVButton>
        </div>
        <template v-if="isLongTerm">
          <div v-if="!hidePrice" class="space-y-2">
            <LabeledText v-if="currentPeriod" :label="currentPeriodLabel">
              <span v-currency="currentPeriodPrice" class="semibold text-base md:text-lg" />
              <span class="ml-2 text-sm font-normal text-obsidian-500"
                >{{ formatCurrency(currentPeriodPerDay) }} / {{ t('day') }}</span
              >
            </LabeledText>
            <LabeledText v-if="nextPeriod" :label="nextPeriodLabel">
              <span v-currency="nextPeriodPrice" class="semibold text-base md:text-lg" />
              <span class="ml-2 text-sm font-normal text-obsidian-500"
                >{{ formatCurrency(nextPeriodPerDay) }} / {{ t('day') }}</span
              >
            </LabeledText>
            <LabeledText :label="t('contractTotal')">
              <span v-currency="contractTotal" class="text-sm md:text-base" />
            </LabeledText>
          </div>
          <LabeledText v-else :label="t('totalPrice')">{{ hidePriceText }}</LabeledText>
        </template>
        <LabeledText v-else :label="t('totalPrice')"
          ><span
            v-if="!hidePrice"
            v-currency="booking.priceCalculation.totalPrice"
            class="semibold text-base md:text-lg"
          /><span v-else>{{ hidePriceText }}</span></LabeledText
        >
      </div>
    </div>

    <div v-if="booking.debitCardPaymentAtHandover && booking.agency">
      <InfoIcon class="inline-icon" />
      {{ t('debitCardPaymentAtHandoverAgent') }}
    </div>
    <div v-else-if="booking.debitCardPaymentAtHandover">
      <InfoIcon class="inline-icon" />
      {{ t('debitCardPaymentAtHandover') }}
    </div>

    <TextareaField name="internalComment" :label="t('internalComment')" :initial-rows="1" />
    <div class="grid items-start gap-4 md:grid-cols-2">
      <div v-if="booking.customerComment" class="rounded-md bg-white p-4">
        <h4 class="font-semibold">{{ t('commentFromCustomer') }}</h4>
        <p class="whitespace-pre-wrap font-normal">{{ booking.customerComment }}</p>
        <template v-if="booking.confirmationReply">
          <h4 class="mt-4 font-semibold">{{ t('confirmationReply') }}</h4>
          <p class="whitespace-pre-wrap font-normal">{{ booking.confirmationReply }}</p>
        </template>
      </div>

      <div v-if="booking.customer?.comment" class="rounded-md bg-white p-4">
        <div class="flex">
          <h4 class="font-semibold">{{ t('commentAboutCustomer') }}</h4>
          <Information v-if="$can('update', 'User', 'comment')">
            <template #modal>
              {{ t('commentAboutCustomerInfo') }}
            </template>
          </Information>
        </div>
        <p class="whitespace-pre-wrap font-normal">{{ booking.customer.comment }}</p>
      </div>
    </div>

    <div v-if="hasChanged" class="flex justify-end gap-4">
      <CVButton variant="default" :is-loading="isSaving" @click.prevent="onSubmit">{{
        t('save')
      }}</CVButton>
      <CVButton variant="secondary" :disabled="isSaving" @click.prevent="onCancel">{{
        t('cancel')
      }}</CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LabeledText from '@/components/LabeledText.vue';
import PhoneLink from '@/components/PhoneLink.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import type { User } from '@/entities/auth/user.entity';
import { UserRole } from '@/entities/auth/user-role.enum';
import { useI18n } from 'vue-i18n';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import TextareaField from '../TextareaField.vue';
import InputField from '../InputField.vue';
import { computed, ref, watch } from 'vue';
import DropdownDotted from '@/components/DropdownDotted.vue';
import { useResendBookingMails } from '@/hooks/use-resend-booking-mails';
import { Alert } from '@/services/alert.service';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { cloneDeep } from 'lodash';
import { useFormHasChanged } from '@/hooks/use-form-has-changed';
import { useRefreshBookingConfirmation, useUpdateBooking } from '@/queries/use-bookings';
import { subject } from '@casl/ability';
import { useAbility } from '@casl/vue';
import BookingsOfUserAmount from './BookingsOfUserAmount.vue';
import { RouterLink, useRouter } from 'vue-router';
import BookingStatusBadge from '@/components/badges/BookingStatusBadge.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { InfoIcon } from 'lucide-vue-next';
import Information from '@/components/Information.vue';
import BookingInlineField from '../BookingInlineField.vue';
import { usePdfPreview } from '@/hooks/use-pdf-preview';
import { downloadBlob, fetchBlobFromUrl, fetchPdfBlobFromUrl } from '@/utils/download-file';
import CopyToClipboardButton from '@/components/CopyToClipboardButton.vue';
import { DateTime } from 'luxon';
import { formatDate } from '@/hooks/use-formatted-date';
import { formatCurrency } from '@/utils/format-numbers';
import {
  bookingStartTimezone,
  getContractTotal,
  getCurrentPricingPeriod,
  getNextPricingPeriod,
  getPeriodAveragePricePerDay,
  getPeriodPrice,
  isLongTermPricing,
} from '@/utils/pricing.utils';

const props = defineProps<{
  booking: Booking;
  showStatus?: boolean;
  hidePrice?: boolean;
  hidePriceText?: string;
  showPreviousBookings?: boolean;
}>();

const emit = defineEmits<{
  (e: 'hasChanged', value: boolean): void;
}>();

const { t } = useI18n();
const router = useRouter();

const creationSourceLabel = computed(() => {
  if (!props.booking.creationSource) return '';
  const source = t(`creationSourceValue.${props.booking.creationSource}`);
  if (!props.booking.salesChannel) return source;
  return `${source} (${t(`salesChannel.${props.booking.salesChannel}`)})`;
});
const isLongTerm = computed(() => isLongTermPricing(props.booking.priceCalculation));
const timezone = computed(() => bookingStartTimezone(props.booking));
const currentPeriod = computed(() => {
  const pricing = props.booking.priceCalculation;
  return isLongTermPricing(pricing)
    ? getCurrentPricingPeriod(pricing, new Date(), timezone.value)
    : null;
});
const nextPeriod = computed(() => {
  const pricing = props.booking.priceCalculation;
  return isLongTermPricing(pricing)
    ? getNextPricingPeriod(pricing, new Date(), timezone.value)
    : null;
});
const currentPeriodPrice = computed(() =>
  currentPeriod.value ? getPeriodPrice(currentPeriod.value) : 0,
);
const currentPeriodPerDay = computed(() =>
  currentPeriod.value ? getPeriodAveragePricePerDay(currentPeriod.value) : 0,
);
const nextPeriodPrice = computed(() => (nextPeriod.value ? getPeriodPrice(nextPeriod.value) : 0));
const nextPeriodPerDay = computed(() =>
  nextPeriod.value ? getPeriodAveragePricePerDay(nextPeriod.value) : 0,
);
const contractTotal = computed(() => getContractTotal(props.booking.priceCalculation));
const currentPeriodLabel = computed(() =>
  currentPeriod.value
    ? t('periodPrice', {
        start: formatDate(currentPeriod.value.startDate, DateTime.DATE_SHORT),
        end: formatDate(currentPeriod.value.endDate, DateTime.DATE_SHORT),
      })
    : t('totalPrice'),
);
const nextPeriodLabel = computed(() =>
  nextPeriod.value
    ? t('nextPeriodPrice', {
        start: formatDate(nextPeriod.value.startDate, DateTime.DATE_SHORT),
        end: formatDate(nextPeriod.value.endDate, DateTime.DATE_SHORT),
      })
    : '',
);

const formatUserDisplayName = (user: User) => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');
  const company = user.company?.trim();
  if (company && [UserRole.AGENCY, UserRole.COMPANY].includes(user.role)) {
    return company;
  }
  return name;
};

const { isPending: isSaving, mutateAsync: updateBooking } = useUpdateBooking();

const { resendConfirmationEmail, resendUpdatedContractImmediately, sendRentalKeyLink } =
  useResendBookingMails(props.booking.id);
const { can } = useAbility();
const { mutateAsync: refreshBookingConfirmation, isPending: isRefreshingBookingConfirmation } =
  useRefreshBookingConfirmation();
const pdfPreview = usePdfPreview();

const isAtLeastConfirmed = computed(
  () =>
    props.booking.status === BookingStatus.CONFIRMED ||
    props.booking.status === BookingStatus.HANDED_OVER ||
    props.booking.status === BookingStatus.CAR_RETURNED ||
    props.booking.status === BookingStatus.FINALIZED,
);

const canFinalizePeriodicInvoicingBooking = computed(
  () => props.booking.isReadyToFinalize && canUpdateToStatus(BookingStatus.FINALIZED),
);

const setBookingToPaid = async () => {
  changeBookingStatus(
    BookingStatus.OPEN,
    t('reallySetBookingToPaidText'),
    t('setBookingToPaid'),
    t('cancel'),
  );
};

const setBookingToConfirmed = async () => {
  const askResendBookingConfirmation = !!props.booking.flags.confirmationSentDate;
  const customerComment = props.booking.customerComment?.trim();

  if (!customerComment || askResendBookingConfirmation) {
    return changeBookingStatus(
      BookingStatus.CONFIRMED,
      t('reallyConfirmBookingText'),
      t('confirmBooking'),
      t('cancel'),
      askResendBookingConfirmation,
    );
  }

  const alertResult = await Alert.fire<string | undefined>({
    icon: 'success',
    showDenyButton: true,
    confirmButtonText: t('confirmBooking'),
    denyButtonText: t('cancel'),
    focusDeny: true,
    text: [t('reallyConfirmBookingText'), '', t('customerCommentInConfirm'), customerComment].join(
      '\n',
    ),
    input: 'textarea',
    inputLabel: t('confirmationReplyHint'),
    inputPlaceholder: t('confirmationReplyPlaceholder'),
    inputValue: '',
  });

  if (!alertResult.isConfirmed) return;

  await updateBooking({
    id: props.booking.id,
    booking: {
      status: BookingStatus.CONFIRMED,
      confirmationReply: alertResult.value?.trim() || null,
    },
  });
};

const setBookingToOpen = () => {
  changeBookingStatus(
    BookingStatus.OPEN,
    t('reallySetBookingToUnconfirmedText'),
    t('setBookingToUnconfirmed'),
    t('cancel'),
  );
};

const setBookingToFloating = () => {
  changeBookingStatus(
    BookingStatus.FLOATING,
    t('reallySetBookingToFloatingText'),
    t('setBookingToFloating'),
    t('cancel'),
  );
};

const finalizePeriodicInvoicingBooking = async () => {
  const alertResult = await Alert.fire<boolean | undefined>({
    text: t('reallyFinalizeBookingText'),
    icon: 'question',
    showDenyButton: true,
    confirmButtonText: t('finalizeBooking'),
    denyButtonText: t('cancel'),
    focusDeny: true,
  });
  if (alertResult.isConfirmed) {
    await updateBooking({
      id: props.booking.id,
      booking: { status: BookingStatus.FINALIZED },
    });
  }
};

const changeBookingStatus = async (
  status: BookingStatus.OPEN | BookingStatus.CONFIRMED | BookingStatus.FLOATING,
  text: string,
  confirmButtonText: string,
  cancelButtonText: string,
  askResendBookingConfirmation?: boolean,
) => {
  const alertResult = await Alert.fire<boolean | undefined>({
    text: text,
    icon: 'success',
    showDenyButton: true,
    confirmButtonText: confirmButtonText,
    denyButtonText: cancelButtonText,
    focusDeny: true,
    ...(askResendBookingConfirmation
      ? {
          input: 'checkbox',
          inputLabel: t('resendBookingConfirmation'),
          inputValue: false,
        }
      : {}),
  });
  if (alertResult.isConfirmed) {
    await updateBooking({
      id: props.booking.id,
      booking: { status },
    });
    if (askResendBookingConfirmation && alertResult.value === true) {
      resendConfirmationEmail(true);
    }
  }
};

const downloadContract = () => {
  if (!props.booking.contractFileUrl) return;
  void pdfPreview.openPdfPreview({
    title: t('contractPdfTitle', { bookingNumber: props.booking.bookingNumber }),
    fileName: `booking-${props.booking.bookingNumber}-contract.pdf`,
    load: () => fetchPdfBlobFromUrl(props.booking.contractFileUrl!),
    errorMessage: t('couldNotLoadPdf'),
  });
};

const downloadContractDirect = async () => {
  if (!props.booking.contractFileUrl) return;
  const blob = await fetchBlobFromUrl(props.booking.contractFileUrl);
  await downloadBlob(blob, `booking-${props.booking.bookingNumber}-contract.pdf`);
};

const downloadBookingConfirmation = async () => {
  if (!props.booking.confirmationFileUrl) return;
  await pdfPreview.openPdfPreview({
    title: t('bookingConfirmationPdfTitle', { bookingNumber: props.booking.bookingNumber }),
    fileName: `booking-${props.booking.bookingNumber}-confirmation.pdf`,
    load: async () => {
      const { fileUrl } = await refreshBookingConfirmation(props.booking.id);
      return fetchPdfBlobFromUrl(fileUrl);
    },
    errorMessage: t('couldNotLoadPdf'),
  });
};

const downloadBookingConfirmationDirect = async () => {
  if (!props.booking.confirmationFileUrl) return;
  const { fileUrl } = await refreshBookingConfirmation(props.booking.id);
  const blob = await fetchBlobFromUrl(fileUrl);
  await downloadBlob(blob, `booking-${props.booking.bookingNumber}-confirmation.pdf`);
};

const canUpdateToStatus = (status: BookingStatus) =>
  can('update', subject('Booking', { ...props.booking, status }), 'status');

const dropdownDottedItems = computed(() => [
  {
    title: t('handover'),
    onClick: () => router.push({ name: 'handoverDetail', params: { id: props.booking.id } }),
    statuses: [BookingStatus.CONFIRMED],
    active: can('handle', 'Booking'),
  },
  {
    title: t('return'),
    onClick: () => router.push({ name: 'returnOverview', params: { id: props.booking.id } }),
    statuses: [BookingStatus.HANDED_OVER],
    active: can('handle', 'Booking'),
  },
  {
    title: t('resendBookingConfirmation'),
    onClick: resendConfirmationEmail,
    active: isAtLeastConfirmed.value && can('sendMails', subject('Booking', props.booking)),
  },
  {
    title: t('sendRentalKeyLink'),
    onClick: sendRentalKeyLink,
    statuses: [BookingStatus.HANDED_OVER],
    active: can('sendMails', subject('Booking', props.booking)),
  },
  {
    title: t('downloadBookingConfirmation'),
    onClick: downloadBookingConfirmation,
    statuses: [
      BookingStatus.CONFIRMED,
      BookingStatus.HANDED_OVER,
      BookingStatus.CAR_RETURNED,
      BookingStatus.FINALIZED,
    ],
    active: !!props.booking.confirmationFileUrl,
  },
  {
    title: t('downloadBookingConfirmationDirect'),
    onClick: downloadBookingConfirmationDirect,
    statuses: [
      BookingStatus.CONFIRMED,
      BookingStatus.HANDED_OVER,
      BookingStatus.CAR_RETURNED,
      BookingStatus.FINALIZED,
    ],
    active: !!props.booking.confirmationFileUrl,
  },
  {
    title: t('setBookingToUnconfirmed'),
    onClick: setBookingToOpen,
    statuses: [BookingStatus.CONFIRMED, BookingStatus.FLOATING],
    active: canUpdateToStatus(BookingStatus.OPEN),
  },
  {
    title: t('confirmBooking'),
    onClick: setBookingToConfirmed,
    statuses: [BookingStatus.OPEN, BookingStatus.FLOATING],
    active: canUpdateToStatus(BookingStatus.CONFIRMED),
  },
  {
    title: t('setBookingToFloating'),
    onClick: setBookingToFloating,
    statuses: [BookingStatus.OPEN, BookingStatus.CONFIRMED],
    active: canUpdateToStatus(BookingStatus.FLOATING),
  },
  {
    title: t('resendUpdatedContractImmediately'),
    onClick: resendUpdatedContractImmediately,
    statuses: [BookingStatus.HANDED_OVER, BookingStatus.CAR_RETURNED],
    active: can('sendMails', subject('Booking', props.booking)),
  },
  {
    title: t('downloadContract'),
    onClick: downloadContract,
    statuses: [BookingStatus.HANDED_OVER, BookingStatus.CAR_RETURNED, BookingStatus.FINALIZED],
    active: !!props.booking.contractFileUrl,
  },
  {
    title: t('downloadContractDirect'),
    onClick: downloadContractDirect,
    statuses: [BookingStatus.HANDED_OVER, BookingStatus.CAR_RETURNED, BookingStatus.FINALIZED],
    active: !!props.booking.contractFileUrl,
  },
]);

const onCancel = () => {
  setValues({
    customerComment: props.booking.customerComment,
    internalComment: props.booking.internalComment,
  });
};

const {
  handleSubmit,
  values: formValues,
  setValues,
} = useForm({
  initialValues: {
    customerComment: props.booking.customerComment,
    internalComment: props.booking.internalComment,
  },
  validationSchema: yup.object({
    customerComment: yup.string().nullable(),
    internalComment: yup.string().nullable(),
  }),
});

const unchangedValues = ref(cloneDeep(formValues));

const onSubmit = handleSubmit(async (values) => {
  await updateBooking({
    id: props.booking.id,
    booking: values,
  });
  unchangedValues.value = cloneDeep(formValues);
});

const hasChanged = useFormHasChanged(unchangedValues, formValues);
watch(hasChanged, () => emit('hasChanged', hasChanged.value));
</script>

<i18n lang="json">
{
  "en": {
    "customerComment": "Customer Comment",
    "internalComment": "Internal Comment",
    "debitCardPaymentAtHandover": "This booking was paid with a debit card at handover. It can be confirmed if the Schufa score is better than H.",
    "renter": "Renter",
    "bookingNumber": "Booking Nr.",
    "createdBy": "Created by",
    "createdVia": " via {source}",
    "creationSourceValue": {
      "ROS": "ROS",
      "WEBSITE": "Website",
      "EXTERNAL_API": "External API"
    },
    "tel": "Tel:",
    "bookingStatus": "Booking Status",
    "confirmBooking": "Confirm Booking",
    "confirmChanges": "Confirm Changes",
    "setBookingToPaid": "Set booking to paid",
    "totalPrice": "Total Price",
    "contractTotal": "Contract total",
    "periodPrice": "{start} – {end}",
    "nextPeriodPrice": "Next: {start} – {end}",
    "day": "day",
    "unconfirmedChanges": "Unconfirmed changes",
    "handover": "Handover",
    "return": "Return",
    "resendBookingConfirmation": "Resend booking confirmation",
    "sendRentalKeyLink": "Send rental overview link",
    "resendUpdatedContractImmediately": "Regenerate + resend contract now",
    "reallyConfirmBookingText": "Do you really want to confirm the Booking?",
    "customerCommentInConfirm": "The customer left the following comment:",
    "confirmationReplyHint": "If you want to reply in the booking confirmation, you can do so here:",
    "confirmationReply": "Reply in booking confirmation",
    "confirmationReplyPlaceholder": "Optional reply included in the confirmation email",
    "setStatusToNotConfirmed": "Set to unconfirmed",
    "reallySetBookingToUnconfirmedText": "Do you really want to set the Booking to unconfirmed?",
    "setBookingToUnconfirmed": "Set to unconfirmed",
    "reallySetBookingToFloatingText": "Do you really want to set the Booking to floating?",
    "reallySetBookingToPaidText": "Do you really want to set the Booking to paid?",
    "finalizeBooking": "Finalize booking",
    "reallyFinalizeBookingText": "Mark this booking as finalized? It will be removed from the manual invoicing list.",
    "setBookingToFloating": "Set to floating",
    "downloadContract": "Show Contract",
    "downloadContractDirect": "Download Contract",
    "downloadBookingConfirmation": "Show Booking Confirmation",
    "downloadBookingConfirmationDirect": "Download Booking Confirmation",
    "contractPdf": "Contract",
    "bookingConfirmationPdf": "Booking Confirmation",
    "contractPdfTitle": "Contract for Booking #{bookingNumber}",
    "bookingConfirmationPdfTitle": "Booking Confirmation #{bookingNumber}",
    "couldNotLoadPdf": "The PDF could not be loaded. Please try again.",
    "insuranceCase": "Insurance Case",
    "commentFromCustomer": "Comment from customer (website)",
    "commentAboutCustomer": "Comment about customer",
    "commentAboutCustomerInfo": "Can be edited in the user",
    "debitCardPaymentAtHandoverAgent": "This booking was made through a known agent. The customer may pay on site at handover. Please confirm the booking regardless of payment if everything else is fine.",
    "externalReference": "External Reference",
    "externalReferenceInfo": "The reference number is the number that the agency uses as the booking number in their own systems. If an agency does not automatically transmit a reference number, we can also add it manually. This ensures that the booking can be easily and quickly found in our system using the agency's reference number. (The search function also works for case numbers from insurance claims.)",
    "insuranceCaseRef": {
      "axa": "AXA {referenceNumber}",
      "roland": "Roland {referenceNumber}",
      "europ": "Europ {referenceNumber}",
      "volvo": "Volvo {referenceNumber}",
      "jlr": "JLR {referenceNumber}"
    }
  },
  "de": {
    "customerComment": "Kunden Kommentar",
    "internalComment": "Interner Kommentar",
    "debitCardPaymentAtHandover": "Diese Buchung wurde per Zahlung vor Ort gebucht. Sie kann bestätigt werden, wenn der Schufa Score besser als H ist.",
    "renter": "Mieter",
    "bookingNumber": "Buchungsnr.",
    "createdBy": "Erstellt von",
    "createdVia": " über {source}",
    "creationSourceValue": {
      "ROS": "ROS",
      "WEBSITE": "Webseite",
      "EXTERNAL_API": "Externe API"
    },
    "tel": "Tel:",
    "bookingStatus": "Buchungsstatus",
    "confirmBooking": "Buchung bestätigen",
    "confirmChanges": "Änderungen bestätigen",
    "setBookingToPaid": "Buchung auf Bezahlt stellen",
    "totalPrice": "Gesamtpreis",
    "contractTotal": "Vertragssumme",
    "periodPrice": "{start} – {end}",
    "nextPeriodPrice": "Nächster: {start} – {end}",
    "day": "Tag",
    "unconfirmedChanges": "Unbestätigte Änderungen",
    "handover": "Übergabe",
    "return": "Rückgabe",
    "resendBookingConfirmation": "Buchungsbestätigung neu senden",
    "sendRentalKeyLink": "Mietübersicht senden",
    "resendUpdatedContractImmediately": "Vertrag jetzt neu erstellen + senden",
    "reallyConfirmBookingText": "Möchtest du die Buchung wirklich bestätigen?",
    "customerCommentInConfirm": "Der Kunde hat folgenden Kommentar angegeben:",
    "confirmationReplyHint": "Wenn Du in der Buchungsbestätigung antworten möchtest, dann kannst Du das hier gleich tun:",
    "confirmationReply": "Antwort in der Buchungsbestätigung",
    "confirmationReplyPlaceholder": "Optionale Antwort, die in der Bestätigungsmail erscheint",
    "reallySetBookingToUnconfirmedText": "Möchtest du die Buchung wirklich auf unbestätigt setzen?",
    "setBookingToUnconfirmed": "Auf unbestätigt setzen",
    "reallySetBookingToFloatingText": "Möchtest du die Buchung wirklich auf schwebend setzen?",
    "reallySetBookingToPaidText": "Möchtest du die Buchung wirklich auf Bezahlt stellen?",
    "finalizeBooking": "Buchung abschließen",
    "reallyFinalizeBookingText": "Diese Buchung wirklich als abgeschlossen markieren? Sie wird aus der Liste zur manuellen Abrechnung entfernt.",
    "setBookingToFloating": "Auf schwebend setzen",
    "downloadContract": "Vertrag anzeigen",
    "downloadContractDirect": "Vertrag herunterladen",
    "downloadBookingConfirmation": "Buchungsbestätigung anzeigen",
    "downloadBookingConfirmationDirect": "Buchungsbestätigung herunterladen",
    "contractPdf": "Vertrag",
    "bookingConfirmationPdf": "Buchungsbestätigung",
    "contractPdfTitle": "Vertrag zur Buchung #{bookingNumber}",
    "bookingConfirmationPdfTitle": "Buchungsbestätigung #{bookingNumber}",
    "couldNotLoadPdf": "Das PDF konnte nicht geladen werden. Bitte versuche es erneut.",
    "insuranceCase": "Versicherungsfall",
    "commentFromCustomer": "Kommentar des Kunden (Webseite)",
    "commentAboutCustomer": "Kommentar über Kunde",
    "commentAboutCustomerInfo": "Kann im User bearbeitet werden",
    "debitCardPaymentAtHandoverAgent": "Die Buchung wurde über einen bekannten Vermittler gebucht. Daher kann der Kunde auch erst vor Ort bei der Übergabe zahlen. Bitte Buchung also unabhängig von Zahlung bestätigen, wenn sonst alles passt.",
    "externalReference": "Externe Referenznummer",
    "externalReferenceInfo": "Die Referenznummer ist die Nummer, die unser Vermittler in seinen eigenen Systemen als Buchungsnummer hat. Falls ein Vermittler keine Referenznummer automatisch überträgt, kann diese auch manuell durch uns hinzugefügt werden. Damit die Buchung auch mit der Referenznummer unseres Vermittlers in unserem System über die Suche einfach & schnell zu finden ist. (Die Suchfunktion geht auch für Aktenzeichen von Versicherungsfällen)",
    "insuranceCaseRef": {
      "axa": "AXA {referenceNumber}",
      "roland": "Roland {referenceNumber}",
      "europ": "Europ {referenceNumber}",
      "volvo": "Volvo {referenceNumber}",
      "jlr": "JLR {referenceNumber}"
    }
  },
  "es": {
    "customerComment": "Comentario del cliente",
    "internalComment": "Comentario interno",
    "debitCardPaymentAtHandover": "Esta reserva se pagó con tarjeta de débito en la entrega. Puede confirmarse si la puntuación Schufa es mejor que H.",
    "renter": "Arrendatario",
    "bookingNumber": "N.º de reserva",
    "createdBy": "Creado por",
    "createdVia": " a través de {source}",
    "creationSourceValue": {
      "ROS": "ROS",
      "WEBSITE": "Sitio web",
      "EXTERNAL_API": "API externa"
    },
    "tel": "Tel.:",
    "bookingStatus": "Estado de la reserva",
    "confirmBooking": "Confirmar reserva",
    "confirmChanges": "Confirmar cambios",
    "setBookingToPaid": "Marcar reserva como pagada",
    "totalPrice": "Precio total",
    "contractTotal": "Total del contrato",
    "periodPrice": "{start} – {end}",
    "nextPeriodPrice": "Siguiente: {start} – {end}",
    "day": "día",
    "unconfirmedChanges": "Cambios sin confirmar",
    "handover": "Entrega",
    "return": "Devolución",
    "resendBookingConfirmation": "Reenviar confirmación de reserva",
    "sendRentalKeyLink": "Enviar resumen de alquiler",
    "reallyConfirmBookingText": "¿Seguro que desea confirmar la reserva?",
    "customerCommentInConfirm": "El cliente ha dejado el siguiente comentario:",
    "confirmationReplyHint": "Si desea responder en la confirmación de reserva, puede hacerlo aquí:",
    "confirmationReply": "Respuesta en la confirmación de reserva",
    "confirmationReplyPlaceholder": "Respuesta opcional incluida en el correo de confirmación",
    "setStatusToNotConfirmed": "Marcar como no confirmada",
    "reallySetBookingToUnconfirmedText": "¿Seguro que desea dejar la reserva como no confirmada?",
    "setBookingToUnconfirmed": "Marcar como no confirmada",
    "reallySetBookingToFloatingText": "¿Seguro que desea poner la reserva en estado pendiente?",
    "reallySetBookingToPaidText": "¿Seguro que desea marcar la reserva como pagada?",
    "setBookingToFloating": "Marcar como pendiente",
    "downloadContract": "Ver contrato",
    "downloadContractDirect": "Descargar contrato",
    "downloadBookingConfirmation": "Ver confirmación de reserva",
    "downloadBookingConfirmationDirect": "Descargar confirmación de reserva",
    "contractPdf": "Contrato",
    "bookingConfirmationPdf": "Confirmación de reserva",
    "contractPdfTitle": "Contrato de la reserva n.º {bookingNumber}",
    "bookingConfirmationPdfTitle": "Confirmación de reserva n.º {bookingNumber}",
    "couldNotLoadPdf": "No se pudo cargar el PDF. Inténtelo de nuevo.",
    "insuranceCase": "Siniestro",
    "commentFromCustomer": "Comentario del cliente (web)",
    "commentAboutCustomer": "Comentario sobre el cliente",
    "commentAboutCustomerInfo": "Se puede editar en el usuario",
    "debitCardPaymentAtHandoverAgent": "Esta reserva se realizó a través de un intermediario conocido. El cliente puede pagar in situ en la entrega. Confirme la reserva con independencia del pago si todo lo demás es correcto.",
    "externalReference": "Referencia externa",
    "externalReferenceInfo": "La referencia es el número que la agencia usa como número de reserva en sus propios sistemas. Si una agencia no envía automáticamente una referencia, también podemos añadirla manualmente. Así la reserva se encuentra fácilmente en nuestro sistema con la referencia de la agencia. (La búsqueda también admite números de expediente de siniestros.)",
    "insuranceCaseRef": {
      "axa": "AXA {referenceNumber}",
      "roland": "Roland {referenceNumber}",
      "europ": "Europ {referenceNumber}",
      "volvo": "Volvo {referenceNumber}",
      "jlr": "JLR {referenceNumber}"
    },
    "resendUpdatedContractImmediately": "Regenerar y reenviar contrato ahora",
    "finalizeBooking": "Finalizar reserva",
    "reallyFinalizeBookingText": "¿Marcar esta reserva como finalizada? Se eliminará de la lista de facturación manual."
  }
}
</i18n>
